import {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useMemo
} from 'react';
import { useSearchParams } from 'react-router';

import queryString from 'query-string';

import { useFetchEvaluation } from '@/hooks/queries/evaluations';
import { useFetchPost } from '@/hooks/queries/posts';
import useMedia, {
  INITIAL_MEDIA_VALUES,
  MediaStateType
} from '@/hooks/useMedia';
import { errorApp } from '@/models/misc';
import { Posts } from '@/models/posts';

export interface PostDetailsProviderType extends MediaStateType {
  postId: number;
  evaluationId: number;
  data?: Posts.Details;
  selectedKeywords: Array<string>;
  setSelectedKeywords: Dispatch<SetStateAction<string[]>>;
  keywordList: Array<string>;
  isLoading: boolean;
  isError: boolean;
  error?: errorApp;
  isSuccess: boolean;
  onRefetch: () => void;
  resetState: () => void;
  minors: Array<Array<Posts.VideoMinorItem | Posts.MinorItem>>;
  logos: Array<Array<Posts.VideoLogoItem | Posts.LogoItem>>;
  hasOcrAdvancedItems: boolean;
  hasOcrOldItems: boolean;
  hasMinorsResults: boolean;
  hasLogosResults: boolean;
  isRecognitionHighlighted0to6: boolean;
  setRecognitionHighlighted0to6: Dispatch<SetStateAction<boolean>>;
  isRecognitionHighlighted7to14: boolean;
  setRecognitionHighlighted7to14: Dispatch<SetStateAction<boolean>>;
  isRecognitionHighlighted15to17: boolean;
  setRecognitionHighlighted15to17: Dispatch<SetStateAction<boolean>>;
  hasVRForMinorsSelected: boolean;
  usedParams: queryString.ParsedQuery<string | number>;
  selectedVisualTab: number | null;
  setSelectedVisualTab: Dispatch<SetStateAction<number | null>>;
}

const INITIAL_VALUES: PostDetailsProviderType = {
  postId: 0,
  evaluationId: 0,
  data: undefined,
  selectedKeywords: [],
  setSelectedKeywords: () => {
    throw new Error('setSelectedKeywords not initialized');
  },
  keywordList: [],
  isLoading: false,
  isError: false,
  error: undefined,
  isSuccess: false,
  onRefetch: () => {},
  resetState: () => {},
  minors: [],
  logos: [],
  hasOcrOldItems: false,
  hasOcrAdvancedItems: false,
  hasMinorsResults: false,
  hasLogosResults: false,
  isRecognitionHighlighted0to6: false,
  setRecognitionHighlighted0to6: () => {
    throw new Error('setRecognitionHighlighted0to6 not initialized');
  },
  isRecognitionHighlighted7to14: false,
  setRecognitionHighlighted7to14: () => {
    throw new Error('setRecognitionHighlighted7to14 not initialized');
  },
  isRecognitionHighlighted15to17: false,
  setRecognitionHighlighted15to17: () => {
    throw new Error('setRecognitionHighlighted15to17 not initialized');
  },
  hasVRForMinorsSelected: false,
  usedParams: {},
  selectedVisualTab: null,
  setSelectedVisualTab: () => {
    throw new Error('setSelectedVisualTab not initialized');
  },
  ...INITIAL_MEDIA_VALUES
};

export const PostDetailsContext =
  createContext<PostDetailsProviderType>(INITIAL_VALUES);

interface Props {
  children: React.JSX.Element;
  evaluationId: number;
  postId: number;
  urlParams: queryString.ParsedQuery<string>;
}

export const PostDetailsProvider = ({
  children,
  evaluationId,
  postId,
  urlParams
}: Props) => {
  const [searchParams] = useSearchParams();
  const mediaState = useMedia();
  const { activeIndex, resetMediaState } = mediaState;

  const params = useMemo(
    () => ({
      evaluationId,
      ...urlParams,
      order: searchParams.get('order') || 'desc',
      sort: searchParams.get('sort') || 'createTime'
    }),
    [evaluationId, urlParams, searchParams]
  );

  const { data, isLoading, isError, error, isSuccess, refetch } = useFetchPost({
    id: postId,
    params
  });

  const { data: evaluationData } = useFetchEvaluation({
    id: evaluationId
  });

  const hasVRForMinorsSelected = !!evaluationData?.requestedServices?.some(
    (service: any) => service.type === 'minors'
  );

  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [selectedVisualTab, setSelectedVisualTab] = useState<number | null>(
    null
  );

  const [isRecognitionHighlighted0to6, setRecognitionHighlighted0to6] =
    useState(INITIAL_VALUES.isRecognitionHighlighted0to6);

  const [isRecognitionHighlighted7to14, setRecognitionHighlighted7to14] =
    useState(INITIAL_VALUES.isRecognitionHighlighted7to14);

  const [isRecognitionHighlighted15to17, setRecognitionHighlighted15to17] =
    useState(INITIAL_VALUES.isRecognitionHighlighted15to17);

  const resetState = () => {
    resetMediaState();
    setSelectedVisualTab(null);
    setSelectedKeywords([]);
    setRecognitionHighlighted0to6(INITIAL_VALUES.isRecognitionHighlighted0to6);
    setRecognitionHighlighted7to14(
      INITIAL_VALUES.isRecognitionHighlighted7to14
    );
    setRecognitionHighlighted15to17(
      INITIAL_VALUES.isRecognitionHighlighted15to17
    );
  };

  const keywordList = useMemo(() => {
    if (!data?.evaluation.keywordMatches) return [];

    const result: string[] = [];

    data?.evaluation.keywordMatches.forEach((item) => {
      if (item.value && !result.includes(item.value)) {
        result.push(item.value);
      }
    });

    return result;
  }, [data]);

  const minors = useMemo(
    () =>
      (data?.media || []).map((media) => {
        const vrOnlyMinors =
          media?.visualRecognition
            ?.find((item) => item.type === 'minors')
            ?.items.filter(
              (item): item is Posts.MinorItem | Posts.VideoMinorItem =>
                'minorsFound' in item || 'coordinates' in item
            ) || [];

        return vrOnlyMinors;
      }),
    [data?.media]
  );

  const logos = useMemo(
    () =>
      (data?.media || []).map((media) => {
        const vrOnlyLogos =
          media?.visualRecognition
            ?.find((item) => item.type === 'logos')
            ?.items.filter(
              (item): item is Posts.LogoItem | Posts.VideoLogoItem =>
                'logoFound' in item || 'coordinates' in item
            ) || [];

        return vrOnlyLogos;
      }),
    [data?.media]
  );

  type OcrAdvItem = Posts.OcrAdvImageItem | Posts.OcrAdvVideoItem;

  const hasOcrAdvancedItems = useMemo(() => {
    const activeMedia = data?.media?.[activeIndex];
    if (!activeMedia) return false;

    const vrOnlyOcrAdv = activeMedia.visualRecognition
      ?.find((item) => item.type === 'ocr-adv')
      ?.items.some((item): item is OcrAdvItem => {
        const hasTextFound = 'textFound' in item;
        const hasContent = 'content' in item;
        return hasTextFound || hasContent;
      });

    return !!vrOnlyOcrAdv;
  }, [data?.media]);

  const hasOcrOldItems = useMemo(() => {
    const activeMedia = data?.media?.[activeIndex];
    if (!activeMedia) return false;

    const vrOnlyOcr = activeMedia.visualRecognition?.some(
      (item): item is Posts.Recognition & { type: 'ocr'; text: string } =>
        item.type === 'ocr'
    );

    return !!vrOnlyOcr;
  }, [data?.media]);

  const hasMinorsResults = useMemo(
    () =>
      data?.media[activeIndex]?.visualRecognition?.some(
        (item) => item.type === 'minors' && item.items.length > 0
      ) || false,
    [data?.media[activeIndex]]
  );

  const hasLogosResults =
    data?.media.some((media) =>
      media.visualRecognition?.some(
        (item) => item.type === 'logos' && item.items.length > 0
      )
    ) ?? false;

  const onRefetch = () => {
    refetch();
  };

  const value: PostDetailsProviderType = {
    evaluationId,
    onRefetch,
    postId,
    data,
    isLoading,
    isError,
    error,
    isSuccess,
    selectedKeywords,
    setSelectedKeywords,
    keywordList,
    resetState,
    minors,
    logos,
    hasOcrAdvancedItems,
    hasOcrOldItems,
    hasMinorsResults,
    hasLogosResults,
    isRecognitionHighlighted0to6,
    setRecognitionHighlighted0to6,
    isRecognitionHighlighted7to14,
    setRecognitionHighlighted7to14,
    isRecognitionHighlighted15to17,
    setRecognitionHighlighted15to17,
    hasVRForMinorsSelected,
    usedParams: params,
    selectedVisualTab,
    setSelectedVisualTab,
    ...mediaState
  };

  return (
    <PostDetailsContext.Provider value={value}>
      {children}
    </PostDetailsContext.Provider>
  );
};
